<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { formatDate } from "@core/helpers"
import type { OptimTurpeTemplateView } from "@modules/courtage/types/optim-turpe"
import { construireReserveFraicheur } from "@modules/courtage/utils/optimTurpeReserveFraicheur"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Dernière synthèse effectuée",
            report: "Télécharger le rapport",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    analyse: OptimTurpeTemplateView
    /**
     * Dernière mesure connue du compteur (`monitoring.lastdate`), servie par la
     * page. Absente : aucune réserve de collecte n'est posée, on ne sert jamais
     * une réserve que l'on n'a pas vérifiée (DEV-1621).
     */
    derniereMesureCompteur?: string | null
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    derniereMesureCompteur: null,
})

const emit = defineEmits<{
    "click:report": []
}>()

// ================================
// COMPUTED
// ================================

/**
 * Sous-titre « Réalisé le … » (fuseau Europe/Paris, voir `formatDate`).
 * @returns Chaîne vide si pas de date exploitable.
 */
const getRealisedAtLabel = computed((): string => {
    if (!props.analyse.createdAt) {
        return ""
    }
    return formatDate(
        props.analyse.createdAt,
        "[Réalisé le ]DD/MM/YYYY[ à ]HH:mm"
    )
})

/**
 * Datation et réserve de fraîcheur du rapport servi (DEV-1621).
 *
 * Les bornes de la fenêtre sont prises telles que le moteur les a formatées
 * (`str_value`) : ses dates brutes sont naïves en heure de Paris, les reformater
 * ici décalerait « 15/02/2025 » d'un jour.
 */
const getReserveFraicheur = computed(() => {
    const data = props.analyse.analysisData ?? {}
    const bornes = String(data.period?.str_value ?? "").split(" - ")
    const du =
        (data.date_from_periode_analyse?.str_value ?? "") || (bornes[0] ?? "")
    const au =
        (data.date_to_periode_analyse?.str_value ?? "") || (bornes[1] ?? "")
    return construireReserveFraicheur({
        calculeLe: props.analyse.createdAt,
        fenetreDu: du,
        fenetreAu: au,
        derniereMesure: props.derniereMesureCompteur,
    })
})

// ================================
// INTERACTIONS
// ================================
/** Demande l’ouverture / téléchargement du rapport PDF. */
function onClickReport(): void {
    emit("click:report")
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL — carte dernière synthèse -->
    <!-- ================================ -->
    <BaseTitledCard
        class="optim-turpe-card-result border border-surface-200 shadow-none"
    >
        <!-- ================================ -->
        <!-- HEADER / EN-TÊTE -->
        <!-- ================================ -->
        <template #header>
            <div
                class="flex flex-col sm:flex-row items-start justify-between gap-4"
            >
                <div class="flex flex-col gap-2">
                    <h3>
                        {{ t("title") }}
                    </h3>
                    <p v-if="getRealisedAtLabel" class="text-sm font-normal!">
                        {{ getRealisedAtLabel }}
                    </p>
                    <!-- DEV-1621 : le rapport porte sa date de calcul et sa fenêtre. -->
                    <p
                        v-if="getReserveFraicheur"
                        class="text-sm font-normal! text-primary-700"
                    >
                        {{ getReserveFraicheur.datation }}
                    </p>
                </div>
                <BaseButton
                    :label="t('report')"
                    icon="pi pi-file-pdf"
                    size="small"
                    @click="onClickReport"
                />
            </div>
        </template>
        <template #content>
            <!-- ================================ -->
            <!-- CONTENU PRINCIPAL -->
            <!-- ================================ -->
            <div class="flex flex-col gap-5">
                <!-- DEV-1621 : réserve explicite quand le compteur ne transmet plus. -->
                <BaseMessage
                    v-if="getReserveFraicheur?.estAlerte"
                    severity="warn"
                    :closable="false"
                >
                    {{ getReserveFraicheur.reserve }}
                </BaseMessage>
                <OptimTurpeResultContent :analyse="analyse" />
                <OptimTurpeResultDetail v-bind="analyse.resultDetail" />
            </div>
        </template>
    </BaseTitledCard>
</template>
